import { atom, selector } from "recoil";
import {
  companyProfileState,
  CreditClearanceCompanies,
} from "@/store/company-profile";
import {
  isGoMakePrintProvider,
  PrintProvider,
  resolvePrintProvider,
} from "@/store/print-provider";

const PRINT_NODE_FEATURE_ID = "5623506c-9e43-429b-89e1-b5f1d63df62b";
export const GOMAKE_MIS_FEATURE_ID = "c242bb4f-1221-4999-b3f3-5c8a8adb090f";
export const GOMAKE_AI_FEATURE_ID = "61bcac05-de33-45ed-ab01-bee49346e8de";

export const FRONT_DESK_FEATURE_ID = "74c6d31c-c120-4246-a24e-0a1854b1cbfb";

export const CUSTOMIZE_GOMAKE_AI_FEATURE_ID =
  "bee4c55f-fb7a-4c6b-81da-56cbc651eac5";

export const printHouseProfile = atom<IPrintHouseProfile>({
  key: "printHouseProfile",
  default: {} as IPrintHouseProfile,
});

/** Print settings tab + print UX are gated by the existing PrintNode feature only. */
export const printHouseUsePrintNode = selector<boolean>({
  key: "printHouseUsePrintNodeSelector",
  get: ({ get }) => {
    const profileState = get(printHouseProfile);
    return (
      profileState.allowedFeaturesId?.includes(PRINT_NODE_FEATURE_ID) ?? false
    );
  },
});

/** Active backend: PrintNode (default) or GoMake Print Manager checkbox. */
export const printHouseActivePrintProvider = selector<PrintProvider>({
  key: "printHouseActivePrintProviderSelector",
  get: ({ get }) => {
    const companyProfile = get(companyProfileState);
    return resolvePrintProvider(companyProfile?.printProvider);
  },
});

/** True when PrintNode feature is on and the house checked Use GoMake Print Manager. */
export const printHouseUsesGoMakePrintProvider = selector<boolean>({
  key: "printHouseUsesGoMakePrintProviderSelector",
  get: ({ get }) =>
    get(printHouseUsePrintNode) &&
    isGoMakePrintProvider(get(printHouseActivePrintProvider)),
});

// Selector to check if print house has packaging slip printer configured (PrintNode or GoMakePrint)
export const printHouseHasPackagingSlipPrinter = selector<boolean>({
  key: "printHouseHasPackagingSlipPrinterSelector",
  get: ({ get }) => {
    if (!get(printHouseUsePrintNode)) {
      return false;
    }

    const profileState = get(printHouseProfile);
    const companyProfile = get(companyProfileState);

    if (get(printHouseUsesGoMakePrintProvider)) {
      return !!companyProfile?.gomakePrintPackagingSlipPrinterId;
    }

    return (
      profileState.packagingSlipPrinterId != null &&
      profileState.packagingSlipPrinterId > 0
    );
  },
});

// Selector to check if print house has Gomake MIS feature
export const printHouseHasMISFeature = selector<boolean>({
  key: "printHouseHasMISFeatureSelector",
  get: ({ get }) => {
    const profileState = get(printHouseProfile);
    return (
      profileState.allowedFeaturesId?.includes(GOMAKE_MIS_FEATURE_ID) ?? false
    );
  },
});

// Selector to check if print house has Gomake AI feature
export const printHouseHasAIFeature = selector<boolean>({
  key: "printHouseHasAIFeatureSelector",
  get: ({ get }) => {
    const profileState = get(printHouseProfile);
    return (
      profileState.allowedFeaturesId?.includes(GOMAKE_AI_FEATURE_ID) ?? false
    );
  },
});

// Selector to check if print house has Customize GomakeAI feature
export const printHouseHasCustomizeAIFeature = selector<boolean>({
  key: "printHouseHasCustomizeAIFeatureSelector",
  get: ({ get }) => {
    const profileState = get(printHouseProfile);
    return (
      profileState.allowedFeaturesId?.includes(
        CUSTOMIZE_GOMAKE_AI_FEATURE_ID
      ) ?? false
    );
  },
});
// Selector to check if print house has Gomake MIS feature
export const printHouseHasFrontDeskFeature = selector<boolean>({
  key: "printHouseHasFrontDeskFeatureSelector",
  get: ({ get }) => {
    const profileState = get(printHouseProfile);
    return (
        profileState.allowedFeaturesId?.includes(FRONT_DESK_FEATURE_ID) ?? false
    );
  },
});

/** Print house uses Stripe for card payments (connected + Stripe selected as clearance company). */
export const printHouseUsesStripePayments = selector<boolean>({
  key: "printHouseUsesStripePaymentsSelector",
  get: ({ get }) => {
    const companyProfile = get(companyProfileState);
    return (
      companyProfile?.creditClearanceCompany === CreditClearanceCompanies.Stripe &&
      companyProfile?.isConnectedToStripe === true
    );
  },
});

export const isUsPrintHouseState = atom<boolean>({
  key: "isUsPrintHouseState",
  default: false,
});

export interface IPrintHouseProfile {
  id: string;
  name: string;
  logo: string | null;
  pdfLogo: string | null;
  loginLogo: string | null;
  country: string;
  filesApiAddress?: string;
  allowedFeaturesId: string[];
  domain?: string;
  isShowOnBoardingMenu?: boolean;
  mailingProviderType?: number;
  stripeCustomerId?: string;
  isTrial?: boolean;
  maxTrialDays?: number;
  remainingTrialDays?: number;
  trialFinished?: boolean;
  packagingSlipPrinterId?: number;
  localFilePathMapping?: string;
  /** Matches admin PrintHouseProfileDTO.ErpType (PrintHouseERP enum underlying value). */
  erpType?: number;
  /** UI theme name from PrintHouses.Theme (e.g. "hp-client"). */
  theme?: string | null;
}
